import { C } from '../../lib/theme';
import { styles } from '../../lib/styles';
import { $, genId } from '../../lib/util';
import type { Case } from '../../domain/initial-cases';
import { Dialog } from '../Dialog';
import { getCurrentUser } from '../../auth/useUser';
import { useAppData } from '../../state/contexts/AppDataContext';
import { useUIState } from '../../state/contexts/UIStateContext';
import { financialBarMet, isVerifiedRef } from '../../domain/financial-bar';

// #695 — AN ADD THAT ALREADY CARRIES THE CALL IS A VERIFICATION.
//
// This modal has always accepted a date and a summary, which means an analyst
// who calls a reference and then records it here is recording a verification,
// not just a name. So it runs the same `financialBarMet` auto-verify as the
// reference panel, in the same PATCH as the add: two verified business
// references plus one verified banking one tick the `financial` KYC item.
//
// Without this the two entry points disagreed — the panel ticked the item and
// the modal did not — which is the same class of bug #695 exists to close, just
// one layer down. The audit code stays FINANCIAL_REF_ADDED because the act IS
// an add; `verified` joins its detail so the row records the state it left the
// item in, exactly as KYC_EVIDENCE_ADDED does for a clear record (#689).
//
// One-way, like every other auto-verify here: it can tick the item and never
// unticks it. Nothing in this modal can remove a reference, so the bar cannot
// fall on this path anyway — but the `||` says so rather than relying on that.
//
// #718 — THE REFERENCE CALL IS THE EVIDENCE, SO THE ADD FILES IT.
//
// Financial Stability used to carry two forms: this one, and the checklist's
// generic Save Evidence Record. An analyst who called a reference either typed
// the call twice or left the Evidence Log empty on an item they had verified.
// So this modal is now the ONLY form on that item (KycTab hides the generic one
// for `financial`), and the add derives an evidence record from the reference
// it is adding — one form, one PATCH, one audit row.
//
// DERIVED, not re-typed: `source` names the institution and what kind of
// reference it is, `searchTerms` carries who was called and when, `findings` is
// the summary of the call, and `result` is the one thing the reference form did
// not already ask for, so it is the one field the Result select adds. The shape
// is KycTab's record shape exactly — same `genId('EVD')`, same `verifiedBy`,
// same ISO `date`, same three `result` values — because the Evidence Log that
// renders it is KycTab's and has not changed.
//
// A LEAD IS NOT A FINDING. A reference saved with neither a date nor a summary
// is a name to call later (#545: the applicant nominates, 382 verifies), and
// `isVerifiedRef` is the portal's existing test for that. Filing an evidence
// record for one would put a findings-less row in the log — and, since Result
// defaults to clear, would auto-verify a required KYC item off a bare name,
// which is the failure #695 exists to prevent. So a lead-only add behaves
// exactly as it did before this issue: the reference, no evidence, and the bar
// still the only thing that can tick the item.
export function AddFinancialRefModal({ c }: { c: Case }) {
  const { updateCase } = useAppData();
  const { setModal } = useUIState();
  const user = getCurrentUser()!;
  return (
    <Dialog title="Add Financial Reference" onClose={() => setModal(null)}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label htmlFor="ref-type" style={{ fontSize: 11, color: C.textMuted }}>Type</label><select id="ref-type" style={{ ...styles.select, width: '100%' }}><option value="business">Business</option><option value="banking">Banking</option></select></div>
          <div><label htmlFor="ref-name" style={{ fontSize: 11, color: C.textMuted }}>Contact Name</label><input id="ref-name" style={styles.input} /></div>
          <div><label htmlFor="ref-title" style={{ fontSize: 11, color: C.textMuted }}>Title/Position</label><input id="ref-title" style={styles.input} /></div>
          <div><label htmlFor="ref-company" style={{ fontSize: 11, color: C.textMuted }}>Company/Institution</label><input id="ref-company" style={styles.input} /></div>
          <div><label htmlFor="ref-date" style={{ fontSize: 11, color: C.textMuted }}>Date of Contact</label><input id="ref-date" type="date" style={styles.input} /></div>
          {/* The only field the reference form did not already ask for, and the
              one the Evidence Log leads with. Same three values and the same
              order as the checklist's own Result select, so the two forms do
              not disagree about what a result can be. */}
          <div><label htmlFor="ref-result" style={{ fontSize: 11, color: C.textMuted }}>Result</label><select id="ref-result" defaultValue="clear" style={{ ...styles.select, width: '100%' }}><option value="clear">✓ Clear</option><option value="flagged">⚠ Flagged</option><option value="inconclusive">? Inconclusive</option></select></div>
        </div>
        <div style={{ marginTop: 12 }}><label htmlFor="ref-summary" style={{ fontSize: 11, color: C.textMuted }}>Summary of Discussion</label><textarea id="ref-summary" style={styles.textarea} /></div>
        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <button style={{ ...styles.btn, ...styles.btnGold }} onClick={() => {
            const ref = {
              type: $('ref-type').value,
              name: $('ref-name').value,
              title: $('ref-title').value,
              company: $('ref-company').value,
              date: $('ref-date').value,
              summary: $('ref-summary').value,
            };
            const result = $('ref-result').value;
            if (ref.name) {
              const nextRefs = [...c.financialRefs, ref];
              const kycField = c.kyc?.financial ?? { verified: false, evidence: [] };
              const was = kycField.verified ?? false;
              const barMet = financialBarMet(nextRefs);
              // The add records a call, so it files the evidence for it.
              const evidence = isVerifiedRef(ref)
                ? {
                    id: genId('EVD'),
                    date: new Date().toISOString(),
                    // Company is optional on a reference (docs/intake-api.md
                    // advertises it so, and #545 renders around its absence),
                    // so the contact's name carries the source when it is
                    // missing rather than leaving "(business reference)".
                    source: `${ref.company || ref.name} (${ref.type} reference)`,
                    // Who 382 called, and when — the two things an approver
                    // re-reading the log needs to find the call again.
                    searchTerms: [ref.name, ref.date].filter((v) => v).join(' · '),
                    result,
                    findings: ref.summary,
                    verifiedBy: user.id,
                  }
                : null;
              // Both auto-verifies, and the one veto over them. #695's bar (two
              // called business references plus one called banking one) and
              // #689's clear record each tick the item on their own — that is
              // what "whichever fires" means, and dropping the second would
              // make removing the generic form a quiet loss of behaviour, since
              // that form has verified this item off a clear record since #689.
              //
              // A FLAGGED result never verifies, and it vetoes the bar too: a
              // call that turned something up is not a completed verification,
              // whatever the arithmetic says. `inconclusive` is silent — it
              // neither verifies on its own nor blocks the bar.
              //
              // `was ||` keeps it one-way, as everywhere else here: nothing on
              // this path can untick an item the analyst ticked by hand.
              const verified = was || (evidence
                ? result !== 'flagged' && (result === 'clear' || barMet)
                : barMet);
              updateCase(c.id, {
                financialRefs: nextRefs,
                // `kyc` is a WHOLE-COLUMN write, so the spread is what keeps
                // every other checklist item. Sent whenever there is an
                // evidence record to file or the flag actually changes; an add
                // that does neither has no business rewriting the column.
                ...(evidence || verified !== was
                  ? {
                      kyc: {
                        ...c.kyc,
                        financial: {
                          ...kycField,
                          ...(evidence ? { evidence: [...(kycField.evidence || []), evidence] } : {}),
                          verified,
                        },
                      },
                    }
                  : {}),
                // One audit row for the whole act. The code stays
                // FINANCIAL_REF_ADDED — no new code, so the nine-file lockstep
                // is untouched — and the detail names the evidence it filed so
                // the row points at the record rather than only implying it.
                action: {
                  code: 'FINANCIAL_REF_ADDED',
                  detail: {
                    type: ref.type, name: ref.name, company: ref.company, verified,
                    evidenceId: evidence?.id ?? null,
                    result: evidence ? result : null,
                  },
                },
              });
              setModal(null);
            }
          }}>Add Reference</button>
          <button style={{ ...styles.btn, ...styles.btnOutline }} onClick={() => setModal(null)}>Cancel</button>
        </div>
    </Dialog>
  );
}
